import { SearchFilterEnum } from "types/enums";

import {
    BooleanFilter,
    DateFilter,
    DateTimeFilter,
    ExternalApiFilter,
    FilterType,
    HistoryFilter,
    LeadCountLimitFilter,
    MultiSelectFilter,
    NumberFilter,
    RatioFilter,
    SelectFilter,
    StringFilter,
    TimeRestrictionFilter,
} from "../types/filter";

const STRING_OPERATORS: SearchFilterEnum[] = [
    SearchFilterEnum.Equal,
    SearchFilterEnum.NotEqual,
    SearchFilterEnum.MultiIlike,
    SearchFilterEnum.NotIlike,
    SearchFilterEnum.StartsWith,
    SearchFilterEnum.NotStartsWith,
    SearchFilterEnum.EndsWith,
    SearchFilterEnum.NotEndsWith,
];

const NUMBER_OPERATORS: SearchFilterEnum[] = [
    SearchFilterEnum.Equal,
    SearchFilterEnum.GreaterThan,
    SearchFilterEnum.GreaterThanOrEqual,
    SearchFilterEnum.NotEqual,
    SearchFilterEnum.LessThan,
    SearchFilterEnum.LessThanOrEqual,
    SearchFilterEnum.Between,
    SearchFilterEnum.NotBetween,
];

const DATE_OPERATORS: SearchFilterEnum[] = [
    SearchFilterEnum.Equal,
    SearchFilterEnum.GreaterThan,
    SearchFilterEnum.GreaterThanOrEqual,
    SearchFilterEnum.NotEqual,
    SearchFilterEnum.LessThan,
    SearchFilterEnum.LessThanOrEqual,
    SearchFilterEnum.Between,
    SearchFilterEnum.NotBetween,
];

const BOOLEAN_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal];

const SELECT_OPERATORS: SearchFilterEnum[] = [
    SearchFilterEnum.Equal,
    SearchFilterEnum.NotEqual,
    SearchFilterEnum.In,
    SearchFilterEnum.NotIn,
];

// Custom select types use single-value inputs — no In/NotIn support
const SINGLE_SELECT_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal, SearchFilterEnum.NotEqual];

const OFFER_SELECT_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.In, SearchFilterEnum.NotIn];

const MULTISELECT_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.In, SearchFilterEnum.NotIn];

const RATIO_OPERATORS: SearchFilterEnum[] = [
    SearchFilterEnum.Equal,
    SearchFilterEnum.GreaterThan,
    SearchFilterEnum.GreaterThanOrEqual,
    SearchFilterEnum.NotEqual,
    SearchFilterEnum.LessThan,
    SearchFilterEnum.LessThanOrEqual,
];

const createFilterType = <T extends FilterType>(filter: T): T => filter;

interface NumberFilterTypeOptions {
    includeNullOperators?: boolean;
}

export const createNumberFilterType = ({
    includeNullOperators = false,
}: NumberFilterTypeOptions = {}): NumberFilter =>
    createFilterType<NumberFilter>({
        type: "number",
        operators: includeNullOperators
            ? [...NUMBER_OPERATORS, SearchFilterEnum.Null, SearchFilterEnum.NotNull]
            : NUMBER_OPERATORS,
    });

const StringFilterType = createFilterType<StringFilter>({
    type: "string",
    operators: STRING_OPERATORS,
});

const NumberFilterType = createNumberFilterType();

const DateFilterType = createFilterType<DateFilter>({
    type: "date",
    operators: DATE_OPERATORS,
});

const DateTimeFilterType = createFilterType<DateTimeFilter>({
    type: "datetime",
    operators: DATE_OPERATORS,
});

const BooleanFilterType = createFilterType<BooleanFilter>({
    type: "boolean",
    operators: BOOLEAN_OPERATORS,
});

const SelectFilterType = createFilterType<SelectFilter>({
    type: "select",
    operators: SELECT_OPERATORS,
});

const CountrySelectFilterType = createFilterType<SelectFilter>({
    type: "countrySelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const OfferSelectFilterType = createFilterType<SelectFilter>({
    type: "offerSelect",
    operators: OFFER_SELECT_OPERATORS,
});

const PartnerSelectFilterType = createFilterType<SelectFilter>({
    type: "partnerSelect",
    operators: MULTISELECT_OPERATORS,
});

const AdvertiserSelectFilterType = createFilterType<SelectFilter>({
    type: "advertiserSelect",
    operators: MULTISELECT_OPERATORS,
});

const CascadeSelectFilterType = createFilterType<SelectFilter>({
    type: "cascadeSelect",
    operators: MULTISELECT_OPERATORS,
});

const CategorySelectFilterType = createFilterType<SelectFilter>({
    type: "categorySelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const FormStructureSelectFilterType = createFilterType<SelectFilter>({
    type: "formStructureSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const StepTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "stepTypeSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const IntegrationStepTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "integrationStepTypeSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const OutgoingPostbackStepTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "outgoingPostbackStepTypeSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const IncomingPostbackStepTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "incomingPostbackStepTypeSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const StructureStepTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "structureStepTypeSelect",
    operators: SINGLE_SELECT_OPERATORS,
});

const ConversionStateSourceSelectFilterType = createFilterType<SelectFilter>({
    type: "string",
    operators: STRING_OPERATORS,
    //type: "conversionStateSourceSelect",
    //operators: SELECT_OPERATORS,
});

const ConversionStateTypeSelectFilterType = createFilterType<SelectFilter>({
    type: "string",
    operators: STRING_OPERATORS,
    //type: "conversionStateTypeSelect",
    //operators: SELECT_OPERATORS,
});

const CountryMultiSelectFilterType = createFilterType<MultiSelectFilter>({
    type: "countryMultiSelect",
    operators: MULTISELECT_OPERATORS,
});

const ConversionStateSourceMultiSelectFilterType = createFilterType<MultiSelectFilter>({
    type: "conversionStateSourceMultiSelect",
    operators: MULTISELECT_OPERATORS,
});

const ConversionStateTypeMultiSelectFilterType = createFilterType<MultiSelectFilter>({
    type: "conversionStateTypeMultiSelect",
    operators: MULTISELECT_OPERATORS,
});

const ConversionStateStatusMultiSelectFilterType = createFilterType<MultiSelectFilter>({
    type: "conversionStateStatusMultiSelect",
    operators: MULTISELECT_OPERATORS,
});

const LabelMultiSelectFilterType = createFilterType<MultiSelectFilter>({
    type: "labelMultiSelect",
    operators: MULTISELECT_OPERATORS,
});

const RatioFilterType = createFilterType<RatioFilter>({
    type: "ratio",
    operators: RATIO_OPERATORS,
});

const HISTORY_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal];

const HistorySentFilterType = createFilterType<HistoryFilter>({
    type: "history_sent",
    operators: HISTORY_OPERATORS,
});

const HistoryRejectedFilterType = createFilterType<HistoryFilter>({
    type: "history_rejected",
    operators: HISTORY_OPERATORS,
});

const HistoryAcceptedFilterType = createFilterType<HistoryFilter>({
    type: "history_accepted",
    operators: HISTORY_OPERATORS,
});

const HistoryRejectedReasonFilterType = createFilterType<HistoryFilter>({
    type: "history_rejected_reason",
    operators: HISTORY_OPERATORS,
});

const EXTERNAL_API_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal];

const ExternalApiFilterType = createFilterType<ExternalApiFilter>({
    type: "external_api",
    operators: EXTERNAL_API_OPERATORS,
});

const TIME_RESTRICTION_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal];

const TimeRestrictionFilterType = createFilterType<TimeRestrictionFilter>({
    type: "time_restriction",
    operators: TIME_RESTRICTION_OPERATORS,
});

const LEAD_COUNT_LIMIT_OPERATORS: SearchFilterEnum[] = [SearchFilterEnum.Equal];

const LeadCountLimitFilterType = createFilterType<LeadCountLimitFilter>({
    type: "lead_count_limit",
    operators: LEAD_COUNT_LIMIT_OPERATORS,
});

const EidFilterType = createFilterType<StringFilter>({
    type: "string",
    operators: [SearchFilterEnum.Equal, SearchFilterEnum.NotEqual],
});

// ----------------------------------------------------------------
// Exported Filter Types Collection
// ----------------------------------------------------------------

export const FILTER_TYPES = {
    // general filter types
    default: StringFilterType,
    string: StringFilterType,
    number: NumberFilterType,
    date: DateFilterType,
    datetime: DateTimeFilterType,
    boolean: BooleanFilterType,
    select: SelectFilterType,
    ratio: RatioFilterType,

    // history filter types
    history_sent: HistorySentFilterType,
    history_rejected: HistoryRejectedFilterType,
    history_accepted: HistoryAcceptedFilterType,
    history_rejected_reason: HistoryRejectedReasonFilterType,

    // external API filter
    external_api: ExternalApiFilterType,

    // time restriction filter
    time_restriction: TimeRestrictionFilterType,

    // lead count limit filter
    lead_count_limit: LeadCountLimitFilterType,

    // custom filter select types
    countrySelect: CountrySelectFilterType,
    offerSelect: OfferSelectFilterType,
    partnerSelect: PartnerSelectFilterType,
    advertiserSelect: AdvertiserSelectFilterType,
    cascadeSelect: CascadeSelectFilterType,
    categorySelect: CategorySelectFilterType,
    formStructureSelect: FormStructureSelectFilterType,
    stepTypeSelect: StepTypeSelectFilterType,
    integrationStepTypeSelect: IntegrationStepTypeSelectFilterType,
    outgoingPostbackStepTypeSelect: OutgoingPostbackStepTypeSelectFilterType,
    incomingPostbackStepTypeSelect: IncomingPostbackStepTypeSelectFilterType,
    structureStepTypeSelect: StructureStepTypeSelectFilterType,
    conversionStateSourceSelect: ConversionStateSourceSelectFilterType,
    conversionStateTypeSelect: ConversionStateTypeSelectFilterType,

    // custom filter multiselect types
    countryMultiSelect: CountryMultiSelectFilterType,
    conversionStateSourceMultiSelect: ConversionStateSourceMultiSelectFilterType,
    conversionStateTypeMultiSelect: ConversionStateTypeMultiSelectFilterType,
    conversionStateStatusMultiSelect: ConversionStateStatusMultiSelectFilterType,
    labelMultiSelect: LabelMultiSelectFilterType,

    eid: EidFilterType,
};

export const DEFAULT_TIME_FILTER_PARAM = "created_at";
